import React, { useEffect } from "react";
import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import MyTheme from "./theme/muiTheme";
import Login from "./containers/Auth/Login";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";

import {
  loginSuccess,
  logout,
  setStation,
  setQuin,
  setAppUser,
  setViewAs,
  ADMIN_UIDS,
} from "./containers/Auth/authSlice";
import { auth, db } from "./config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

import Dashboard from "../src/containers/Dashboard/Dashboard";
import AdminMenu from "./containers/Admin/AdminMenu";
import AdminValidation from "./containers/Admin/AdminValidation";
import AdminCorrection from "./containers/Admin/AdminCorrection";
import Redirector from "./components/Redirector";

function ProtectedRoute({ redirectTo, children }) {
  const { userId } = useSelector((state) => state.auth);
  return userId ? children : <Navigate to={redirectTo} replace />;
}

function AdminRoute({ children }) {
  const { userId, isAdmin, role } = useSelector((state) => state.auth);
  if (!userId) return <Navigate to="/login" replace />;
  if (!isAdmin && role !== "gestock")
    return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOnlyRoute({ children }) {
  const { userId, isAdmin } = useSelector((state) => state.auth);
  if (!userId) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <Redirector />,
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/menu",
      element: (
        <AdminRoute>
          <AdminMenu />
        </AdminRoute>
      ),
    },
    {
      path: "/validate",
      element: (
        <AdminOnlyRoute>
          <AdminValidation />
        </AdminOnlyRoute>
      ),
    },
    {
      path: "/correction",
      element: (
        <AdminRoute>
          <AdminCorrection />
        </AdminRoute>
      ),
    },
    {
      path: "/dashboard",
      element: (
        <ProtectedRoute redirectTo="/login">
          <Dashboard />
        </ProtectedRoute>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
}

const App = () => {
  const dispatch = useDispatch();
  const [authReady, setAuthReady] = React.useState(false);

  useEffect(() => {
    const loadStationName = async (uid) => {
      if (ADMIN_UIDS.includes(uid)) return; // admins have no station
      try {
        const userDocRef = doc(db, "users", uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const stationName = userDoc.data().station;
          if (stationName) dispatch(setStation(stationName));
          const quinValue = userDoc.data().quin;
          if (quinValue) dispatch(setQuin(quinValue));
        }
      } catch (error) {
        console.error("Error loading station name:", error);
      }
    };

    const loadAppUser = async (uid) => {
      try {
        const snap = await getDoc(doc(db, "app_users", uid));
        if (snap.exists()) {
          dispatch(setAppUser(snap.data()));
        }
      } catch (e) {
        // Non-blocking — user might not be in app_users (e.g. station users)
      }
    };

    const storedUid = localStorage.getItem("userId");
    if (storedUid) {
      dispatch(loginSuccess(storedUid));
      loadStationName(storedUid);
      loadAppUser(storedUid);

      // Restore viewAs state if it was saved (for Gestock or station-impersonation)
      const savedViewAs = localStorage.getItem("viewAsUid");
      const savedStation = localStorage.getItem("viewAsStation");
      const savedQuin = localStorage.getItem("viewAsQuin");
      if (savedViewAs || savedStation) {
        dispatch(
          setViewAs({
            uid: savedViewAs || null,
            station: savedStation || null,
            quin: savedQuin || null,
          }),
        );
      }
    }

    const unsubscribe =
      auth && auth.onAuthStateChanged
        ? auth.onAuthStateChanged((user) => {
            if (user) {
              dispatch(loginSuccess(user.uid));
              localStorage.setItem("userId", user.uid);
              loadStationName(user.uid);
              loadAppUser(user.uid);
            } else if (localStorage.getItem("userId")) {
              // userId mémorisé sans session Firebase (session expirée, ou
              // bascule prod ↔ staging) : toutes les lectures seraient refusées
              // et les pages resteraient sur « Chargement… » → retour au login.
              dispatch(logout());
            }
            setAuthReady(true);
          })
        : undefined;

    if (!auth || !auth.onAuthStateChanged) setAuthReady(true);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [dispatch]);

  if (!authReady) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" size={48} />
        <div style={{ marginTop: 16, fontSize: 18 }}>Chargement...</div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={MyTheme}>
      <AppRouter />
    </ThemeProvider>
  );
};

export default App;
